import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type StatColor = 'accent' | 'green' | 'orange' | 'red';

const COLOR_VAR: Record<StatColor, string> = {
  accent: 'var(--color-accent)',
  green: 'var(--color-green)',
  orange: 'var(--color-orange)',
  red: 'var(--color-red)',
};

export interface StatCardProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly subtext?: ReactNode;
  readonly trend?: 'up' | 'down' | 'flat';
  readonly color?: StatColor;
  readonly werkOs?: boolean;
  readonly pulse?: boolean;
  readonly loading?: boolean;
}

export function StatCard({
  label,
  value,
  subtext,
  trend,
  color = 'accent',
  werkOs = false,
  pulse = false,
  loading = false,
}: StatCardProps): JSX.Element {
  const [flashKey, setFlashKey] = useState(0);
  const lastValue = useRef<string>(stringify(value));

  useEffect(() => {
    const next = stringify(value);
    if (next !== lastValue.current) {
      lastValue.current = next;
      setFlashKey((k) => k + 1);
    }
  }, [value]);

  return (
    <article
      className="relative rounded-md border border-border bg-card overflow-hidden p-4"
      style={pulse ? { animation: 'pulse-glow 1.6s ease-in-out infinite', color: COLOR_VAR[color] } : undefined}
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{
          background: `linear-gradient(90deg, ${COLOR_VAR[color]} 0%, transparent 80%)`,
        }}
      />
      {werkOs && (
        <span
          className="absolute top-2 right-2 font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-sm border border-border text-accent"
          style={{ backgroundColor: 'rgba(0,196,232,0.06)' }}
        >
          WERK·OS
        </span>
      )}
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-text-dim">
        {label}
      </div>
      {loading ? (
        <div className="mt-2 h-8 w-20 rounded bg-card-2 animate-shimmer" />
      ) : (
        <div
          key={flashKey}
          className="mt-1 text-2xl font-semibold animate-value-flash rounded px-1 -mx-1"
          style={{ color: 'var(--color-text)' }}
        >
          {value}
        </div>
      )}
      {(subtext || trend) && (
        <div className="mt-1 flex items-center gap-2 text-xs text-text-dim">
          {trend && <TrendArrow direction={trend} />}
          {subtext && <span>{subtext}</span>}
        </div>
      )}
    </article>
  );
}

function TrendArrow({ direction }: { readonly direction: 'up' | 'down' | 'flat' }): JSX.Element {
  const symbol = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '→';
  const color =
    direction === 'up'
      ? 'var(--color-green)'
      : direction === 'down'
        ? 'var(--color-red)'
        : 'var(--color-text-dim)';
  return (
    <span aria-hidden className="text-[10px]" style={{ color }}>
      {symbol}
    </span>
  );
}

function stringify(value: ReactNode): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
}
